import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Phone, Video, Users, Loader, X, CheckCircle, Mic, MicOff, VideoOff, ShieldAlert, RefreshCw } from 'lucide-react';
import { MedicalFilesPanel } from '../components/MedicalFilesPanel';
import { SessionTimer } from '../components/SessionTimer';
import { PermissionPromptCard } from '../components/PermissionPromptCard';
import { toast } from 'sonner';
import { ArrowRight, Clock } from 'lucide-react';
import { rememberCallOrigin } from '../lib/paymentReturn';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Friendly CTA that replaces the generic error banner when microphone/camera
// acquisition fails. It explains what to do instead of just showing a red alert.
const PermissionCTA = ({ issue, t, onRetry, onDismiss }) => {
  const isVideo = issue.forVideo;
  const titles = {
    denied: t(
      isVideo ? 'يرجى السماح بالميكروفون والكاميرا' : 'يرجى السماح بالميكروفون',
      isVideo ? 'Please allow microphone & camera' : 'Please allow microphone',
      isVideo ? 'Veuillez autoriser micro & caméra' : 'Veuillez autoriser le micro'
    ),
    not_found: t(
      isVideo ? 'لم يتم العثور على ميكروفون/كاميرا' : 'لم يتم العثور على ميكروفون',
      isVideo ? 'No microphone or camera detected' : 'No microphone detected',
      isVideo ? 'Aucun micro/caméra détecté' : 'Aucun micro détecté'
    ),
    in_use: t(
      'الميكروفون/الكاميرا قيد الاستخدام بواسطة تطبيق آخر',
      'Microphone/camera is being used by another app',
      'Micro/caméra utilisé par une autre application'
    ),
    other: t(
      'تعذر الوصول إلى الميكروفون/الكاميرا',
      'Could not access microphone/camera',
      "Impossible d'accéder au micro/caméra"
    ),
  };
  const helps = {
    denied: t(
      'انقر على أيقونة القفل بجانب عنوان الموقع، اختر "أذونات" وفعّل الميكروفون' + (isVideo ? ' والكاميرا' : '') + '، ثم اضغط إعادة المحاولة.',
      'Click the lock icon next to the URL, choose Permissions, and allow Microphone' + (isVideo ? ' and Camera' : '') + ', then tap Retry.',
      "Cliquez sur l'icône de cadenas à côté de l'URL, choisissez Autorisations et activez le micro" + (isVideo ? ' et la caméra' : '') + ", puis réessayez."
    ),
    not_found: t(
      'تأكد من توصيل سماعة/ميكروفون' + (isVideo ? ' أو كاميرا' : '') + ' بالجهاز ثم اضغط إعادة المحاولة.',
      'Make sure a microphone' + (isVideo ? ' and camera' : '') + ' is connected to your device, then tap Retry.',
      'Assurez-vous qu\'un micro' + (isVideo ? ' et une caméra' : '') + ' est connecté, puis réessayez.'
    ),
    in_use: t(
      'أغلق التطبيقات الأخرى التي تستخدم الميكروفون/الكاميرا (مثل Zoom، Teams) ثم اضغط إعادة المحاولة.',
      'Close other apps using the microphone/camera (Zoom, Teams, etc.) and tap Retry.',
      'Fermez les autres applications utilisant le micro/caméra puis réessayez.'
    ),
    other: t(
      'حدث خطأ غير متوقع. اضغط إعادة المحاولة، أو أنهِ المكالمة وأعد المحاولة لاحقاً.',
      'An unexpected error occurred. Tap Retry, or end the call and try again later.',
      "Une erreur s'est produite. Réessayez ou réessayez plus tard."
    ),
  };
  return (
    <Card className="mb-4 p-5 border-2 border-amber-300 bg-amber-50/60" data-testid="permission-cta">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
          <ShieldAlert size={22} className="text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base md:text-lg font-extrabold text-amber-900" data-testid="permission-cta-title">
            {titles[issue.kind] || titles.other}
          </h3>
          <p className="text-sm text-amber-900/80 mt-1 leading-relaxed">
            {helps[issue.kind] || helps.other}
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              onClick={onRetry}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold"
              data-testid="permission-cta-retry"
            >
              <RefreshCw size={16} className="me-2" />
              {t('إعادة المحاولة', 'Retry', 'Réessayer')}
            </Button>
            <Button
              onClick={onDismiss}
              variant="outline"
              className="rounded-xl"
              data-testid="permission-cta-dismiss"
            >
              {t('متابعة بدون الإذن', 'Continue without it', 'Continuer sans')}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export const InstantCallPage = () => {
  const { doctorId } = useParams();
  const [searchParams] = useSearchParams();
  const callType = searchParams.get('type') || 'voice';
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [doctor, setDoctor] = useState(null);
  const [step, setStep] = useState('form');
  const [callId, setCallId] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [error, setError] = useState('');
  const [redirectInfo, setRedirectInfo] = useState(null); // {originalName, newName} when overflow kicks in
  const [permissionIssue, setPermissionIssue] = useState(null); // { kind: 'denied'|'not_found'|'in_use'|'other', forVideo: bool }
  const [loading, setLoading] = useState(false);
  const [agoraConfig, setAgoraConfig] = useState(null);
  // Subscription gate — populated by /api/subscriptions/can-call so we can
  // show a "Please subscribe first" CTA before the medical form.
  const [subGate, setSubGate] = useState({ loading: true, allowed: true, reason: null });
  // Consultations left after the call ends — drives the "buy another" CTA.
  const [remainingAfter, setRemainingAfter] = useState(null);
  const [mediaState, setMediaState] = useState({ mic: true, cam: callType === 'video', remoteJoined: false });
  const pollRef = useRef(null);
  const clientRef = useRef(null);
  const localTracksRef = useRef({ audio: null, video: null });
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  const [medicalForm, setMedicalForm] = useState({
    current_symptoms: '',
    additional_notes: ''
  });

  // If the user previously tapped "Maybe Later" on /permissions, re-prompt them
  // here BEFORE they fill the medical form. We initialize from localStorage so
  // there's no flash for users who already granted.
  const [showPermissionGate, setShowPermissionGate] = useState(() => {
    try {
      const status = localStorage.getItem('tababa_perm_status');
      // Only "granted" suppresses; "skipped", null, or anything else re-prompts.
      return status !== 'granted';
    } catch (e) {
      return true;
    }
  });

  useEffect(() => {
    axios.get(`${API}/doctors/${doctorId}`).then(r => setDoctor(r.data)).catch(() => setError(t('لم يتم العثور على الطبيب', 'Doctor not found', 'Médecin introuvable')));
    axios.get(`${API}/config`).then(r => setAgoraConfig(r.data)).catch(() => {});
    // Subscription pre-flight — the /calls/request endpoint returns 402 if
    // the patient has no active plan or no remaining consultations. Checking
    // here lets us show a friendly "Subscribe first" CTA instead.
    axios.get(`${API}/subscriptions/can-call`, { withCredentials: true })
      .then(r => setSubGate({ loading: false, allowed: !!r.data?.allowed, reason: r.data?.reason || null }))
      .catch(() => setSubGate({ loading: false, allowed: false, reason: 'unknown' }));
    // On tab-close / refresh / navigation-away, notify the backend so the
    // call doesn't get stuck in "active" if the patient never taps End.
    const beforeUnload = () => {
      const cid = callIdRef.current;
      if (!cid) return;
      try {
        const url = `${API}/calls/${cid}/end`;
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([JSON.stringify({})], { type: 'application/json' }));
        }
      } catch (_e) { /* best effort */ }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      window.removeEventListener('beforeunload', beforeUnload);
      cleanupAgora();
    };
  }, [doctorId]);

  // Keep an always-fresh ref to callId for the beforeunload closure above,
  // which is captured once on mount.
  const callIdRef = React.useRef(null);
  React.useEffect(() => {
    callIdRef.current = callId;
  }, [callId]);

  const cleanupAgora = async () => {
    try {
      if (localTracksRef.current.audio) { localTracksRef.current.audio.close(); }
      if (localTracksRef.current.video) { localTracksRef.current.video.close(); }
      if (clientRef.current) { await clientRef.current.leave(); }
    } catch (e) {
      console.warn('Agora cleanup error:', e);
    }
    localTracksRef.current = { audio: null, video: null };
    clientRef.current = null;
  };

  const joinAgoraChannel = async (channelName) => {
    if (!agoraConfig?.agora_enabled || !agoraConfig?.agora_app_id) {
      return;
    }
    // Idempotency guard - prevent duplicate joins when both handleStartCall and polling fire
    if (clientRef.current) {
      return;
    }
    let client;
    try {
      // Get token
      const uid = Math.floor(Math.random() * 1000000);
      let tokRes;
      try {
        tokRes = await axios.get(`${API}/agora/token?channel=${channelName}&uid=${uid}`, { withCredentials: true });
      } catch (authErr) {
        if (authErr.response?.status === 401) {
          setError(t('انتهت جلستك. يرجى تسجيل الدخول مرة أخرى', 'Your session expired. Please log in again', 'Session expirée'));
          return;
        }
        throw authErr;
      }
      const { token, app_id } = tokRes.data;

      client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      clientRef.current = client;

      // Subscribe to remote users
      client.on('user-published', async (remoteUser, mediaType) => {
        await client.subscribe(remoteUser, mediaType);
        if (mediaType === 'video' && remoteVideoRef.current) {
          remoteUser.videoTrack.play(remoteVideoRef.current);
        }
        if (mediaType === 'audio') {
          remoteUser.audioTrack.play();
        }
        setMediaState((s) => ({ ...s, remoteJoined: true }));
      });

      client.on('user-unpublished', () => {
        setMediaState((s) => ({ ...s, remoteJoined: false }));
      });

      // Doctor / pharmacist left the channel → end the call server-side.
      // This handles the case where the provider clicks End Call and their
      // page tears down before hitting /end for us, or their tab crashed.
      client.on('user-left', () => {
        setMediaState((s) => ({ ...s, remoteJoined: false }));
        const cid = callIdRef.current;
        if (cid) {
          axios.post(`${API}/calls/${cid}/end`, {}, { withCredentials: true }).catch(() => {});
        }
      });

      await client.join(app_id, channelName, token, uid);

      // Notify the counterpart (doctor) that the patient has entered the room.
      // Fire-and-forget; if it fails the flow is unaffected.
      const cid = callIdRef.current;
      if (cid) {
        axios.post(`${API}/calls/${cid}/enter`, {}, { withCredentials: true }).catch(() => {});
      }

      await attemptLocalTracks(client);
    } catch (e) {
      console.error('Agora join error:', e);
      setError(t('فشل بدء البث الصوتي/المرئي', 'Failed to start audio/video', 'Échec audio/vidéo'));
      // Cleanup partial state
      if (client) {
        try { await client.leave(); } catch (_) { /* ignore */ }
      }
      clientRef.current = null;
    }
  };

  // Classify an Agora track-creation error so we can show the right CTA copy.
  const classifyMediaError = (err) => {
    const code = err?.code || err?.name || '';
    const msg = (err?.message || '').toLowerCase();
    if (code === 'PERMISSION_DENIED' || code === 'NotAllowedError' || msg.includes('permission')) return 'denied';
    if (code === 'DEVICE_NOT_FOUND' || code === 'NotFoundError' || msg.includes('not found') || msg.includes('no device')) return 'not_found';
    if (code === 'DEVICE_IN_USE' || code === 'NotReadableError' || msg.includes('in use') || msg.includes('busy')) return 'in_use';
    return 'other';
  };

  // Try to create + publish local mic (and camera for video calls). Used at join time and on retry.
  const attemptLocalTracks = async (clientArg) => {
    const client = clientArg || clientRef.current;
    if (!client) return false;
    try {
      // Close any existing tracks before retrying
      if (localTracksRef.current.audio) { try { localTracksRef.current.audio.close(); } catch (_) { /* ignore */ } localTracksRef.current.audio = null; }
      if (localTracksRef.current.video) { try { localTracksRef.current.video.close(); } catch (_) { /* ignore */ } localTracksRef.current.video = null; }

      const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      localTracksRef.current.audio = audioTrack;
      const tracksToPublish = [audioTrack];

      if (callType === 'video') {
        const videoTrack = await AgoraRTC.createCameraVideoTrack();
        localTracksRef.current.video = videoTrack;
        tracksToPublish.push(videoTrack);
        if (localVideoRef.current) videoTrack.play(localVideoRef.current);
      }

      await client.publish(tracksToPublish);
      setPermissionIssue(null);
      setError('');
      return true;
    } catch (trackErr) {
      console.error('Local track error:', trackErr);
      setPermissionIssue({ kind: classifyMediaError(trackErr), forVideo: callType === 'video' });
      return false;
    }
  };

  const handleStartCall = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/calls/request`, {
        doctor_id: doctorId,
        call_type: callType,
        medical_form: medicalForm
      }, { withCredentials: true });

      setCallId(res.data.call_id);
      setCallStatus(res.data);

      // If the backend redirected us via the overflow chain, capture both names
      // so we can show "you were redirected because Dr X is busy".
      if (res.data.redirected_from && res.data.doctor_name) {
        setRedirectInfo({
          originalName: doctor?.full_name || '',
          newName: res.data.doctor_name,
        });
        toast.info(
          t(
            `تم توجيهك إلى ${res.data.doctor_name}`,
            `You've been redirected to ${res.data.doctor_name}`,
            `Vous avez été redirigé vers ${res.data.doctor_name}`
          ),
          { duration: 7000 }
        );
      }

      setStep(res.data.status === 'active' ? 'active' : 'waiting');
      startPolling(res.data.call_id);

      if (res.data.status === 'active') {
        joinAgoraChannel(res.data.channel_name);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('حدث خطأ', 'An error occurred', 'Erreur'));
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/calls/status/${id}`, { withCredentials: true });
        setCallStatus(res.data);
        if (res.data.status === 'active' && step !== 'active') {
          setStep('active');
          joinAgoraChannel(res.data.channel_name);
        }
        if (res.data.status === 'ended' || res.data.status === 'cancelled') {
          clearInterval(pollRef.current);
          await cleanupAgora();
          setStep('ended');
          refreshRemaining();
        }
      } catch (e) { /* keep polling on transient errors */ console.warn('Call status poll failed:', e?.message || e); }
    }, 3000);
  };

  // Refresh the remaining-consultations count once the call is over.
  const refreshRemaining = async () => {
    try {
      const r = await axios.get(`${API}/subscriptions/my-subscription`, { withCredentials: true });
      setRemainingAfter(r.data?.consultations_remaining ?? null);
    } catch { setRemainingAfter(null); }
  };

  const handleCancelCall = async () => {
    if (!callId) return;
    try {
      await axios.delete(`${API}/calls/${callId}`, { withCredentials: true });
    } catch (e) {
      console.warn('Cancel call request failed (continuing teardown):', e?.message || e);
    }
    if (pollRef.current) clearInterval(pollRef.current);
    await cleanupAgora();
    navigate('/doctors?category=doctors');
  };

  // Psychology sessions only: 5-minutes-left ping for both participants.
  const handleSessionWarning = (remaining) => {
    const msg = remaining > 240
      ? t('تبقّى 5 دقائق على انتهاء الجلسة', '5 minutes left in this session', '5 minutes restantes')
      : t('الجلسة تقارب على الانتهاء', 'This session is about to end', 'La séance va se terminer');
    toast.warning(msg, { duration: 8000 });
    if (callId) {
      axios.post(`${API}/calls/${callId}/session-warning`, {}, { withCredentials: true }).catch(() => {});
    }
  };


  const handleEndCall = async () => {
    if (!callId) return;
    try {
      await axios.post(`${API}/calls/${callId}/end`, {}, { withCredentials: true });
    } catch (e) {
      console.warn('End call request failed (continuing teardown):', e?.message || e);
    }
    if (pollRef.current) clearInterval(pollRef.current);
    await cleanupAgora();
    setStep('ended');
    refreshRemaining();
  };

  // 3-minute cap reached — close the call automatically.
  const handleTimeUp = async () => {
    toast.info(t('انتهت مدة الاستشارة (3 دقائق)', 'Consultation time is up (3 minutes)', 'Temps écoulé (3 min)'), { duration: 8000 });
    await handleEndCall();
  };


  const toggleMic = async () => {
    const t = localTracksRef.current.audio;
    if (!t) return;
    await t.setEnabled(!mediaState.mic);
    setMediaState((s) => ({ ...s, mic: !s.mic }));
  };

  const toggleCam = async () => {
    const t = localTracksRef.current.video;
    if (!t) return;
    await t.setEnabled(!mediaState.cam);
    setMediaState((s) => ({ ...s, cam: !s.cam }));
  };

  if (!doctor && !error) {
    return <div className="flex items-center justify-center min-h-screen"><Loader className="animate-spin text-[#1e88e5]" size={48} /></div>;
  }

  return (
    <div className={`min-h-screen bg-gray-50 py-8 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4 max-w-3xl">
        {permissionIssue && (
          <PermissionCTA
            issue={permissionIssue}
            t={t}
            onRetry={async () => { await attemptLocalTracks(); }}
            onDismiss={() => setPermissionIssue(null)}
          />
        )}
        {!permissionIssue && error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        {/* Overflow notice — explains why the caller is talking to a different doctor */}
        {redirectInfo && step !== 'form' && (
          <Card className="mb-4 p-4 border-l-4 border-l-amber-400 bg-amber-50/60" data-testid="overflow-banner">
            <div className={`flex items-start gap-3 ${language === 'ar' ? 'flex-row-reverse text-right' : ''}`}>
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <ArrowRight size={18} className="text-amber-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-amber-900 text-sm md:text-base" data-testid="overflow-banner-title">
                  {t(
                    `تم توجيهك إلى الطبيب ${redirectInfo.newName} لأن الطبيب ${redirectInfo.originalName} مشغول`,
                    `You've been redirected to Dr. ${redirectInfo.newName} because Dr. ${redirectInfo.originalName} is busy`,
                    `Vous avez été redirigé vers Dr ${redirectInfo.newName} car Dr ${redirectInfo.originalName} est occupé`
                  )}
                </p>
                <p className="text-amber-800/80 text-xs md:text-sm mt-1">
                  {t(
                    'سيقدم لك هذا الطبيب نفس مستوى الرعاية. شكراً لصبرك.',
                    'This doctor will give you the same level of care. Thanks for your patience.',
                    'Ce médecin vous donnera le même niveau de soin. Merci de votre patience.'
                  )}
                </p>
              </div>
            </div>
          </Card>
        )}

        {step === 'form' && doctor && !subGate.loading && !subGate.allowed && (
          <Card className="p-8 text-center" data-testid="call-subscription-gate">
            <div className="inline-flex w-20 h-20 bg-[#1e88e5]/10 rounded-full items-center justify-center text-[#1e88e5] mb-4">
              <ShieldAlert size={36} />
            </div>
            <h2 className="text-2xl font-bold text-[#2c3e50] mb-2">
              {subGate.reason === 'exhausted'
                ? t('استنفدت استشاراتك', 'You are out of consultations', 'Consultations épuisées')
                : subGate.reason === 'expired'
                ? t('انتهت صلاحية اشتراكك', 'Your subscription has expired', 'Abonnement expiré')
                : t('تحتاج إلى اشتراك', 'A subscription is required', 'Un abonnement est requis')
              }
            </h2>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
              {subGate.reason === 'exhausted'
                ? t('لقد استخدمت جميع المكالمات في باقتك. جدد اشتراكك للاستمرار.',
                    'You have used every consultation in your current plan. Renew or upgrade to continue.',
                    "Vous avez utilisé toutes les consultations. Renouvelez pour continuer.")
                : t('اختر باقة لتفعيل المكالمات الصوتية والفيديو مع الأطباء.',
                    'Pick a plan to unlock voice and video consultations with our doctors.',
                    "Choisissez un forfait pour activer les consultations.")
              }
            </p>
            <Button
              onClick={() => {
                // Remember the list this doctor came from so checkout returns
                // the patient there instead of the packages page.
                rememberCallOrigin(`/doctors?category=${doctor?.category === 'pharmacy' ? 'pharmacy' : 'doctors'}`);
                navigate('/subscriptions');
              }}
              className="bg-[#1e88e5] hover:bg-[#1976d2] text-white h-12 px-8 rounded-xl text-base"
              data-testid="call-gate-subscribe-btn"
            >
              {t('عرض الباقات', 'View Plans', 'Voir les forfaits')}
              <ArrowRight size={18} className="ms-2" />
            </Button>
          </Card>
        )}

        {step === 'form' && doctor && (subGate.loading || subGate.allowed) && (
          <Card className="p-6" data-testid="call-form-step">
            <div className="text-center mb-6">
              <div className="inline-flex w-20 h-20 bg-[#1e88e5] rounded-full items-center justify-center text-white mb-3">
                {callType === 'video' ? <Video size={36} /> : <Phone size={36} />}
              </div>
              <h2 className="text-2xl font-bold text-[#2c3e50]">{t('استشارة فورية', 'Instant Consultation', 'Consultation instantanée')}</h2>
              <p className="text-gray-600">{t('مع', 'with', 'avec')} <strong>{doctor.full_name}</strong></p>
              <p className="text-sm text-gray-500 mt-1">{callType === 'video' ? t('مكالمة فيديو', 'Video Call', 'Appel vidéo') : t('مكالمة صوتية', 'Voice Call', 'Appel vocal')}</p>
            </div>

            <div
              className="mb-5 rounded-xl bg-blue-50 text-[#1565c0] text-sm font-semibold px-4 py-3 text-center"
              data-testid="call-duration-notice"
            >
              {t('مدة الاستشارة 3 دقائق وتُغلق تلقائياً بعد انتهائها',
                 'Consultations last 3 minutes and close automatically when the time is up',
                 'Les consultations durent 3 minutes et se terminent automatiquement')}
            </div>


            {/* Re-prompt users who tapped "Maybe Later" on /permissions */}
            {showPermissionGate && (
              <div className="mb-5">
                <PermissionPromptCard
                  needVideo={callType === 'video'}
                  onGranted={() => setShowPermissionGate(false)}
                />
              </div>
            )}

            <form onSubmit={handleStartCall} className="space-y-4">
              <p className="text-sm text-gray-600">
                {t('شارك ما تشعر به فقط — ليس عليك إدخال أي بيانات شخصية إضافية',
                  'Share only what you are feeling — no other personal details needed',
                  'Partagez seulement ce que vous ressentez — aucun autre détail requis')}
              </p>

              <div>
                <Label>{t('بماذا تشعر الآن؟', 'What are you feeling?', 'Que ressentez-vous ?')} <span className="text-red-500">*</span></Label>
                <Textarea
                  value={medicalForm.current_symptoms}
                  onChange={(e) => setMedicalForm({ ...medicalForm, current_symptoms: e.target.value })}
                  required
                  rows={3}
                  placeholder={t('اشرح أعراضك بإيجاز...', 'Briefly describe your symptoms...', 'Décrivez vos symptômes...')}
                  className="mt-2"
                  data-testid="call-symptoms"
                />
              </div>

              <div>
                <Label>{t('ملاحظات إضافية (اختياري)', 'Additional Notes (Optional)', 'Notes (Optionnel)')}</Label>
                <Textarea
                  value={medicalForm.additional_notes}
                  onChange={(e) => setMedicalForm({ ...medicalForm, additional_notes: e.target.value })}
                  rows={2}
                  placeholder={t('أي شيء يجب على الطبيب معرفته...', 'Anything the doctor should know...', 'Tout ce que le médecin doit savoir...')}
                  className="mt-2"
                  data-testid="call-notes"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => navigate(-1)} className="flex-1">{t('إلغاء', 'Cancel', 'Annuler')}</Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-[#1e88e5] hover:bg-[#1565c0]" data-testid="start-call-button">
                  {loading ? t('جاري...', 'Starting...', 'Démarrage...') : t('ابدأ الاتصال', 'Start Call', 'Démarrer')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {step === 'waiting' && callStatus && (
          <Card className="p-8 text-center" data-testid="call-waiting-step">
            <div className="inline-flex w-24 h-24 bg-yellow-100 rounded-full items-center justify-center mb-4">
              <Users size={48} className="text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#2c3e50] mb-2">{t('في قائمة الانتظار', 'In Waiting List', 'En attente')}</h2>
            <p className="text-gray-600 mb-6">{t('الطبيب مشغول حالياً مع مريض آخر', 'The doctor is currently busy', 'Médecin occupé')}</p>
            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <div className="text-sm text-gray-600 mb-1">{t('موقعك في القائمة', 'Your position', 'Votre position')}</div>
              <div className="text-5xl font-bold text-[#1e88e5]" data-testid="queue-position">#{callStatus.position}</div>
              {typeof callStatus.estimated_wait_minutes === 'number' && (
                <div
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-sm font-bold text-[#0d47a1] shadow-sm"
                  data-testid="queue-eta"
                >
                  <Clock size={15} />
                  {callStatus.estimated_wait_minutes <= 1
                    ? t('دورك تقريباً الآن', 'Almost your turn', 'Bientôt votre tour')
                    : t(`حوالي ${callStatus.estimated_wait_minutes} دقيقة انتظار`,
                        `About ${callStatus.estimated_wait_minutes} minutes wait`,
                        `Environ ${callStatus.estimated_wait_minutes} min`)}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-3">{t('سيتم إعلامك عند جاهزية الطبيب — يمكنك إبقاء الصفحة مفتوحة', 'You will be notified when the doctor is ready — keep this page open', 'Vous serez informé')}</div>
            </div>
            <Button onClick={handleCancelCall} variant="outline" className="w-full" data-testid="cancel-waiting">
              <X size={18} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
              {t('إلغاء الاتصال', 'Cancel Call', 'Annuler')}
            </Button>
          </Card>
        )}

        {step === 'active' && callStatus && (
          <Card className="overflow-hidden" data-testid="call-active-step">
            {callStatus.category === 'psychology' ? (
              <div className="bg-[#0d47a1] text-white px-4 py-2 flex items-center justify-between gap-2">
                <p className="text-sm font-bold">{t('الجلسة النفسية', 'Psychology session', 'Séance')}</p>
                <SessionTimer
                  elapsedSeconds={callStatus.session_elapsed_seconds}
                  minutes={callStatus.session_minutes}
                  onWarning={handleSessionWarning}
                />
              </div>
            ) : (
              <div className="bg-[#0d47a1] text-white px-4 py-2 flex items-center justify-between gap-2">
                <p className="text-sm font-bold">
                  {t('مدة الاستشارة', 'Consultation time', 'Durée')}
                </p>
                <SessionTimer
                  elapsedSeconds={callStatus.session_elapsed_seconds}
                  minutes={callStatus.session_minutes}
                  onExpire={handleTimeUp}
                />
              </div>
            )}
            {!agoraConfig?.agora_enabled && (
              <Alert className="rounded-none bg-yellow-50 border-yellow-200 border-b">
                <AlertDescription className="text-yellow-800 text-sm">
                  {t(
                    'إعدادات Agora غير مكتملة. الاتصال يعمل بدون صوت/فيديو حتى يضيف المسؤول البيانات.',
                    'Agora not configured. Call works without audio/video until admin adds credentials.',
                    'Agora non configuré.'
                  )}
                </AlertDescription>
              </Alert>
            )}

            {callType === 'video' ? (
              <div className="relative bg-gray-900 aspect-video">
                <div ref={remoteVideoRef} className="w-full h-full flex items-center justify-center text-white">
                  {!mediaState.remoteJoined && (
                    <div className="text-center">
                      <div className="relative inline-flex w-24 h-24 mb-4">
                        <div className="absolute inset-0 bg-white/20 rounded-full animate-ping"></div>
                        <div className="relative w-24 h-24 bg-[#1e88e5] rounded-full flex items-center justify-center">
                          <Video size={48} />
                        </div>
                      </div>
                      <p className="text-lg">{t('في انتظار الطبيب...', 'Waiting for the doctor...', 'En attente du médecin...')}</p>
                    </div>
                  )}
                </div>
                <div ref={localVideoRef} className="absolute bottom-4 right-4 w-32 h-24 bg-black rounded-lg overflow-hidden border-2 border-white"></div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-[#1565c0] to-[#1e88e5] py-16 text-center text-white">
                <div className="relative inline-flex w-32 h-32 mb-4">
                  <div className="absolute inset-0 bg-white/20 rounded-full animate-ping"></div>
                  <div className="relative w-32 h-32 bg-white/30 backdrop-blur rounded-full flex items-center justify-center">
                    <Phone size={56} />
                  </div>
                </div>
                <p className="text-xl font-bold mb-1">{doctor?.full_name}</p>
                <p className="text-blue-100">
                  {mediaState.remoteJoined
                    ? t('متصل', 'Connected', 'Connecté')
                    : t('جاري الاتصال...', 'Connecting...', 'Connexion...')}
                </p>
              </div>
            )}

            <div className="p-4 flex justify-center gap-3 bg-gray-50">
              <Button
                onClick={toggleMic}
                variant={mediaState.mic ? 'outline' : 'destructive'}
                size="lg"
                className="rounded-full w-14 h-14 p-0"
                data-testid="toggle-mic"
              >
                {mediaState.mic ? <Mic size={20} /> : <MicOff size={20} />}
              </Button>

              {callType === 'video' && (
                <Button
                  onClick={toggleCam}
                  variant={mediaState.cam ? 'outline' : 'destructive'}
                  size="lg"
                  className="rounded-full w-14 h-14 p-0"
                  data-testid="toggle-cam"
                >
                  {mediaState.cam ? <Video size={20} /> : <VideoOff size={20} />}
                </Button>
              )}

              <Button onClick={handleEndCall} variant="destructive" size="lg" className="rounded-full w-14 h-14 p-0" data-testid="end-call-button">
                <Phone size={20} className="rotate-[135deg]" />
              </Button>
            </div>
          </Card>
        )}

        {/* In-call file sharing: visible during an active call so patient can send X-rays / lab results to the doctor in real time */}
        {step === 'active' && (
          <Card className="mt-4 p-4" data-testid="in-call-file-share">
            <div className="mb-3">
              <h3 className="text-base font-bold text-[#0d47a1]">
                {t('إرسال ملفات للطبيب أثناء المكالمة',
                  'Send Files to Doctor During the Call',
                  'Envoyer des fichiers pendant l\'appel')}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {t('ارفع تحاليلك أو صور الأشعة أو أي مستند طبي — يراها الطبيب فوراً',
                  'Upload lab results, X-rays or any medical document — visible to the doctor instantly',
                  'Téléchargez analyses, radios ou documents — visibles immédiatement par le médecin')}
              </p>
            </div>
            <MedicalFilesPanel />
          </Card>
        )}

        {step === 'ended' && (
          <Card className="p-8 text-center" data-testid="call-ended-step">
            <div className="inline-flex w-20 h-20 bg-green-100 rounded-full items-center justify-center mb-4">
              <CheckCircle size={48} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#2c3e50] mb-2">{t('انتهت المكالمة', 'Call Ended', 'Terminé')}</h2>
            <p className="text-gray-600 mb-4">{t('شكراً لاستخدامك طبابة', 'Thank you for using Tababa', 'Merci')}</p>

            {remainingAfter !== null && remainingAfter !== -1 && (
              <div
                className={`mx-auto mb-6 max-w-sm rounded-xl px-4 py-3 text-sm font-semibold ${remainingAfter > 0 ? 'bg-blue-50 text-[#1565c0]' : 'bg-amber-50 text-amber-800'}`}
                data-testid="call-ended-balance"
              >
                {remainingAfter > 0
                  ? t(`تبقّى لديك ${remainingAfter} مكالمة`, `${remainingAfter} consultation(s) left in your plan`)
                  : t('لم يتبقَّ لديك أي مكالمة. اشترِ مكالمة أخرى للمتابعة.',
                      'You have no consultations left. Buy another call to continue.')}
              </div>
            )}

            {remainingAfter === 0 ? (
              <Button
                onClick={() => {
                  rememberCallOrigin(`/doctors?category=${doctor?.category === 'pharmacy' ? 'pharmacy' : 'doctors'}`);
                  navigate('/subscriptions');
                }}
                className="w-full bg-[#1e88e5] hover:bg-[#1565c0]"
                data-testid="call-ended-buy-again-btn"
              >
                {t('اشترِ مكالمة أخرى', 'Buy another call', 'Acheter un autre appel')}
                <ArrowRight size={18} className="ms-2" />
              </Button>
            ) : (
              <Button onClick={() => navigate('/patient')} className="w-full bg-[#1e88e5] hover:bg-[#1565c0]">
                {t('العودة إلى لوحة المعلومات', 'Back to Dashboard', 'Retour')}
              </Button>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};
